import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import {
  useAccount,
  useIntersectionObserver,
  useMediaQueries,
  useShop,
  useGoogleApis,
  useSessionStorage
} from 'shop/hooks'
import FullscreenModal from 'shop/components/Modal/FullscreenModal'
import { useContext, useEffect, useRef, useState } from 'react'
import AddressSuggestions, { isAddressEligible } from './AddressSuggestions'
import AddressInputBar from './AddressInputBar'
import { CustomerAddress } from 'shop/types'
import SavedAddressItem from './SavedAddressItem'
import GeolocationAddressItem from './GeolocationAddressItem'
import { GeocodedAddress } from './utils'
import type { CurrentLocationState, PartnerStoreBrowse } from '../types'
import BrowseStoresList from '../LandingStoreList/BrowseStoresList'
import LineTextDivider from 'shop/components/Dividers/LineTextDivider'
import { LandingContext } from '../LandingContext'
import LoginCTA from './LoginCTA'
import StoreSortControl from '../LandingStoreList/StoreSort/StoreSortControl'
import { getPartnerEnabledFulfillmentTypes } from '../utils'

type Props = {
  searchValue: string
  address: string
  onChange: (newAddress: string) => void
  onSelect: ({
    newAddress,
    geocodedAddress
  }: {
    newAddress?: string
    geocodedAddress?: GeocodedAddress
  }) => void
  onClose: () => void
  customerAddresses?: CustomerAddress[]
  selectCustomerAddress: (customerAddress: CustomerAddress) => void
  inputPlaceholder: string
  showLocationSelect: boolean
  stores?: PartnerStoreBrowse[]
  closeModalOnSelect: boolean
}

const LocationSelectModal = ({
  searchValue,
  address,
  onChange,
  onSelect,
  onClose,
  customerAddresses,
  selectCustomerAddress,
  inputPlaceholder,
  showLocationSelect,
  stores,
  closeModalOnSelect
}: Props) => {
  const { isMobile } = useMediaQueries()
  const { areGoogleApisLoaded } = useGoogleApis()
  const { removeSessionStorageItem: removeSavedCurrentLocation } =
    useSessionStorage<CurrentLocationState>('saved-current-location')
  const [inputValue, setInputValue] = useState(searchValue || address || '')
  const [hasUserChangedInput, setHasUserChangedInput] = useState(false)
  const [isControlHeaderStuck, setIsControlHeaderStuck] =
    useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const { isUserLoggedIn } = useAccount()
  const isLoggedIn = isUserLoggedIn()
  const { isSortedPartnerStoresLoading, partner } = useShop()
  const { setDefaultLoadCustomerAddress } = useContext(LandingContext)

  const { isDeliveryEnabled, isPickupEnabled } =
    getPartnerEnabledFulfillmentTypes(partner?.stores || [])

  const hasStores = !!stores?.length

  const hasSavedAddresss = !!customerAddresses?.length
  const showUserAddressOptions = !isAddressEligible(inputValue)

  const showSuggestions = !!isAddressEligible(inputValue) && hasUserChangedInput

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    if (!hasUserChangedInput) setHasUserChangedInput(true)
    setInputValue(inputValue)
    onChange(inputValue)
  }

  const handleClearInput = () => {
    setInputValue('')
    onChange('')
    removeSavedCurrentLocation()
    setDefaultLoadCustomerAddress(false)
    if (inputRef.current) inputRef.current.focus()
  }

  // On Load, autofocus input after animation finishes
  // 300ms slide up animation + 50ms render delay (see ./FormControls.tsx)
  // + 100ms for component to settle
  useEffect(() => {
    window.scrollTo({ top: 0 })
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus()
    }, 450)
  }, [])

  useEffect(() => {
    // when the modal doesn't close, set the input value to the selected address
    if (!closeModalOnSelect && address) setInputValue(address)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address])

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    setIsControlHeaderStuck(!entries[0].isIntersecting)
  }

  // Create intersectionObs to check if controlHeader has become sticky
  const controlHeaderObserver = useIntersectionObserver(
    handleIntersection,
    sentinelRef.current,
    {
      root: null,
      threshold: 0
    }
  )
  useEffect(() => {
    return () => {
      controlHeaderObserver?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Do not render component until google maps is loaded
  if (!areGoogleApisLoaded) return null

  return (
    <FullscreenModal
      id='location-select-modal'
      onClose={onClose}
      isLandingColumn={!isMobile}
      hideRootOnModalShow={false}
      styles={{
        padding: '0',
        transform: `translateY(${showLocationSelect ? '0' : '100vh'})`
      }}
    >
      <ControlsHeaderSentinel ref={sentinelRef} />
      <ControlsHeaderSticky isControlHeaderStuck={isControlHeaderStuck}>
        <AddressInputBar
          inputValue={inputValue}
          handleInputChange={handleInputChange}
          inputRef={inputRef}
          handleClearInput={handleClearInput}
          onClose={onClose}
          inputPlaceholder={inputPlaceholder}
        />
        {isDeliveryEnabled && isPickupEnabled && hasStores && (
          <StoreSortControlContainer
            isControlHeaderStuck={isControlHeaderStuck}
          >
            <StoreSortControl isControlHeaderStuck={isControlHeaderStuck} />
          </StoreSortControlContainer>
        )}
      </ControlsHeaderSticky>
      <ContentContainer>
        <AddressSection>
          {showUserAddressOptions && (
            <>
              <GeolocationAddressItem onSelect={onSelect} />
              {hasSavedAddresss && (
                <SavedAddressItemsContainer>
                  {customerAddresses?.map((customerAddress, index) => (
                    <SavedAddressItem
                      key={`saved-address-${index}`}
                      customerAddress={customerAddress}
                      selectCustomerAddress={selectCustomerAddress}
                    />
                  ))}
                </SavedAddressItemsContainer>
              )}
            </>
          )}
          {/* Do not render persisting suggestions when input is empty or if a user is yet to edit */}
          {showSuggestions && (
            <AddressSuggestions
              inputValue={inputValue}
              onSelect={onSelect}
              setHasUserChangedInput={setHasUserChangedInput}
            />
          )}
          {!isLoggedIn && <LoginCTA />}
        </AddressSection>
        {hasStores && (
          <StoresContainer>
            {!stores[0].distanceFromPoint &&
              isAddressEligible(inputValue) &&
              !isSortedPartnerStoresLoading && (
                <LineTextDivider text='Matching Store Names' />
              )}
            <BrowseStoresList
              stores={!isSortedPartnerStoresLoading ? stores : []}
              inModal={true}
            />
          </StoresContainer>
        )}
      </ContentContainer>
    </FullscreenModal>
  )
}

const ContentContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px 0 0 0',

  [theme.mediaQueries.viewport7]: {
    padding: '16px 20px 0 20px'
  }
}))

const SavedAddressItemsContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  })
)

const StoresContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  padding: '0 16px',

  [theme.mediaQueries.viewport7]: {
    padding: '0 12px'
  }
}))

const AddressSection = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
}))

const StoreSortControlContainer = styled.div<
  StyledHTMLElement & { isControlHeaderStuck: boolean }
>(({ theme, isControlHeaderStuck }) => ({
  padding: isControlHeaderStuck ? '8px 16px' : '12px 16px',
  borderBottom: '1px solid #D9D9D9',
  transition: 'all 200ms ease-in',

  [theme.mediaQueries.viewport7]: {
    padding: isControlHeaderStuck ? '8px 32px' : '18px 32px'
  }
}))

export const ControlsHeaderSentinel = styled.div<StyledHTMLElement>(() => ({
  height: '1px',
  marginTop: '-1px'
}))

export const ControlsHeaderSticky = styled.div<
  StyledHTMLElement & { isControlHeaderStuck: boolean }
>(({ theme, isControlHeaderStuck }) => ({
  position: 'sticky',
  top: 0,
  backgroundColor: '#fff',
  zIndex: theme.zIndex.addressSelectionModal + 1,

  boxShadow: isControlHeaderStuck ? '0px 3px 6px -4px rgba(0, 0, 0, 0.2)' : ''
}))

export default LocationSelectModal
